import {
	ContextActionBar,
	OptionsMenu,
	PageContentTitle,
	SearchInputForm,
	SearchPlaceholder
} from "client/Components";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { FolderNavigationStackItem } from "../../WebDav/useFolderNavigationStack";
import { WebDavBreadCrumb } from "../../WebDav/WebDavBreadCrumb";
import { WebDavPickerOverview } from "./WebDavPickerOverview";
import { newGuid } from "client_legacy/ndaw/guid";
import { AlertManager, useAlerts } from "client/Hooks";
import { DocumentPageLayout } from "../../DocumentPageLayout";
import { DataSourceSortableState, DataSourceSortDirection } from "client/Data/sorting";
import { GridItem } from "../../WebDav/WebDavOverview";
import { CommonItemPickerOptions, InlineCreationOptions } from "../ItemPicker";
import { subscribe, WebDavEvent} from "client_legacy/Documents/WebDav/events";
import { unsubscribe } from "pubsub-js";
import { webDavSortMenuOptions } from "../../WebDav/WebDavPageContent";
import { localize } from "client_legacy/ndaw/culture";
import {
	getContent,
	FileSystemItemModel,
	WebDavServerModel
} from "client/Api/Documents/WebDav/webDav";
import { WebDavConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { NewFolderDialogInitialData, useNewFolderDialog } from "../../WebDav/Dialogs/NewFolderDialog";
import { NewDocumentDialogInitialData, useNewDocumentDialog } from "../../WebDav/Dialogs/NewDocumentDialog";
import {
	ActionAndOthers,
	ActionKind,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { NewDocumentType } from "client/Api/Documents/common";

export type ItemSelectorProps = {
	stack: readonly FolderNavigationStackItem[];
	onFilesSelect: (files: readonly GridItem[]) => void;
	selectedItems: readonly GridItem[];
	commonOptions: CommonItemPickerOptions;
	isMobile: boolean;
	onOpenFolder(folder: FolderNavigationStackItem): void;
	currentFolder: Folder;
	shouldOpenFileOnClick: boolean;
	webDavConfiguration: WebDavConfiguration;
};

export const ItemSelector = (
	props: ItemSelectorProps
): ReactElement => {
	const {
		stack,
		onFilesSelect,
		selectedItems,
		commonOptions,
		isMobile,
		onOpenFolder,
		currentFolder,
		shouldOpenFileOnClick,
		webDavConfiguration
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [loadResult, setLoadResult] = useState<LoadResultState>({ wasSuccessful: true, result: [] });
	const [searchTerm, setSearchTerm] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid(),
	});

	const [sorting, setSorting] = useState<DataSourceSortableState<GridItem>>({
		propertyName: "nameSorting",
		direction: DataSourceSortDirection.Ascending
	});

	const alertManager: AlertManager = useAlerts();
	const [newFolderDialog, openNewFolderDialog] = useNewFolderDialog();
	const [newDocumentDialog, openNewDocumentDialog] = useNewDocumentDialog();

	const documentCreatedHandler = useCallback(
		(document: FileSystemItemModel): void => {
			setLoadResult((loadResult: LoadResultState): LoadResultState => {
				if (loadResult.wasSuccessful) {
					return {
						wasSuccessful: true,
						result: [
							...loadResult.result,
							document
						]
					};
				}
				return loadResult;
			});
			if (document.isFolder) {
				onOpenFolder(document);
			}
		},
		[onOpenFolder]
	);

	useEffect(
		(): (() => void) | undefined => {
			if (!currentFolder) {
				return;
			}

			async function loadModels(
				serverId: number,
				folderId: string,
				signal: AbortSignal
			): Promise<void> {
				setLoadResult({
					wasSuccessful: true,
					result: []
				});

				setIsBusy(true);


				try {
					const models: readonly FileSystemItemModel[] = await getContent(
						serverId,
						folderId,
						signal
					);
					setIsBusy(false);
					setLoadResult({ wasSuccessful: true, result: models });
				} catch (e: unknown) {
					if (isCancelledError(e)) {
						return;
					}
					setIsBusy(false);
					setLoadResult({ wasSuccessful: false });
				}
			}

			const cancellation = new AbortController();

			loadModels(
				currentFolder.server.id,
				currentFolder.id,
				cancellation.signal
			);

			return () => cancellation.abort();
		},
		[currentFolder, alertManager]
	);

	useEffect(
		() => {
			setSearchTerm({
				value: "",
				version: newGuid(),
			});
		},
		[currentFolder]
	);

	useEffect(
		() => {
			const subscriptionTokens = subscribe(
				WebDavEvent.CreatedOrReplaced,
				(_, data) => documentCreatedHandler(data.item)
			);

			return () => {
				unsubscribe(subscriptionTokens);
			};
		},
		[documentCreatedHandler]
	);

	const actions = useMemo(
		(): readonly WebDavPickerAction[] => {
			return getActions(
				onFilesSelect,
				openNewFolderDialog,
				openNewDocumentDialog
			);
		},
		[
			onFilesSelect,
			openNewDocumentDialog,
			openNewFolderDialog
		]
	);

	const actionContext = useMemo(
		(): WebDavPickerActionContext => {
			return {
				folderId: currentFolder.id,
				server: currentFolder.server,
				webDavConfiguration,
				selectedItems,
				inlineCreationOptions: commonOptions.creationOptions
					?? { folder: true },
				isMobile
			};
		},
		[
			commonOptions.creationOptions,
			currentFolder.id,
			currentFolder.server,
			isMobile,
			selectedItems,
			webDavConfiguration
		]
	);

	const shouldShowActionBar = useMemo(
		(): boolean => {
			if (isMobile) {
				return true;
			}

			return selectedItems.length > 0;
		},
		[
			selectedItems.length,
			isMobile
		]
	);


	return (
		<DocumentPageLayout
			trackingId="webdav-picker-page-content"
		>
			<DocumentPageLayout.Header>
				<DocumentPageLayout.BreadCrumb>
					<WebDavBreadCrumb
						items={stack}
						onOpenFolder={onOpenFolder}
					/>
				</DocumentPageLayout.BreadCrumb>
				{shouldShowActionBar
					? (
						<DocumentPageLayout.ActionBarContainer>
							<ContextActionBar
								actions={actions}
								context={actionContext}
								trackingId="global-action-bar"
								horizontalAlignment={isMobile ? "default" : "page-content"}
								hasBorderBottom={isMobile}
							/>
						</DocumentPageLayout.ActionBarContainer>
					)
					: (
						<PageContentTitle
							actions={actions}
							actionContext={actionContext}
							title={stack.length > 0
								? stack[stack.length - 1].name
								: ""
							}
						/>
					)
				}
				<DocumentPageLayout.SortAndFilter>
					<SearchInputForm
						value={searchTerm}
						onChange={(value, version) => setSearchTerm({ value, version })}
						placeholder={
							<SearchPlaceholder
								kind="search"
								properties={[localize("_Name")]}
							/>
						}
						height="large"
					/>
					{isMobile && (
						<OptionsMenu
							value={sorting}
							onChange={setSorting}
							options={webDavSortMenuOptions}
							trackingId="sort-menu"
							title={localize("_SortBy")}
							buttonSize="large"
						/>
					)}
				</DocumentPageLayout.SortAndFilter>
			</DocumentPageLayout.Header>

			<DocumentPageLayout.Content>
				{currentFolder && loadResult.wasSuccessful && (
					<WebDavPickerOverview
						folder={currentFolder}
						models={loadResult.result}
						isLoadingModels={isBusy}
						onOpenFolder={onOpenFolder}
						onFilesSelect={onFilesSelect}
						selectedItems={selectedItems}
						setIsBusy={setIsBusy}
						searchTerm={searchTerm}
						sorting={sorting}
						setSorting={setSorting}
						commonOptions={commonOptions}
						isMobile={isMobile}
						shouldOpenFileOnClick={shouldOpenFileOnClick}
						webDavConfiguration={webDavConfiguration}
					/>
				)}
			</DocumentPageLayout.Content>
			{newFolderDialog}
			{newDocumentDialog}
		</DocumentPageLayout>
	);
};
ItemSelector.displayName = "ItemSelector";

type WebDavPickerActionContext = {
	selectedItems: readonly GridItem[];
	folderId: string;
	server: WebDavServerModel;
	webDavConfiguration: WebDavConfiguration;
	inlineCreationOptions: InlineCreationOptions;
	isMobile: boolean;
};

type WebDavPickerAction = ActionAndOthers<WebDavPickerActionContext>;

function getActions(
	onFilesSelected: (files: readonly GridItem[]) => void,
	openNewFolderDialog: (props: NewFolderDialogInitialData) => Promise<void>,
	openNewDocumentDialog: (props: NewDocumentDialogInitialData) => Promise<void>
): readonly WebDavPickerAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => deselectAllTitle(c.selectedItems.length),
			canExecute: (c) => c.selectedItems.length > 0,
			execute: () => onFilesSelected([]),
			icon: "circle-xmark"
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => !c.isMobile
				&& c.selectedItems.length > 0
		},
		{
			kind: ActionKind.Regular,
			name: "new-folder",
			title: localize("_NewFolder"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.folder),
			execute: (c) => openNewFolderDialog({
				serverId: c.server
					.id,
				folderId: c.folderId
			}),
			icon: { kind: "svg", name: "folder" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-word-document",
			title: localize("_NewWordDocument"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.word),
			execute: (c) => openNewDocumentDialog({
				server: c.server,
				folderId: c.folderId,
				webDavConfiguration: c.webDavConfiguration,
				documentType: NewDocumentType.Word
			}),
			icon: { kind: "svg", name: "docx" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-excel-document",
			title: localize("_NewExcelWorksheet"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.excel),
			execute: (c) => openNewDocumentDialog({
				server: c.server,
				folderId: c.folderId,
				webDavConfiguration: c.webDavConfiguration,
				documentType: NewDocumentType.Excel
			}),
			icon: { kind: "svg", name: "xlsx" }
		},
		{
			kind: ActionKind.Regular,
			name: "new-power-point-document",
			title: localize("_NewPowerPointPresentation"),
			canExecute: (c) => Boolean(c.inlineCreationOptions.powerpoint),
			execute: (c) => openNewDocumentDialog({
				server: c.server,
				folderId: c.folderId,
				webDavConfiguration: c.webDavConfiguration,
				documentType: NewDocumentType.PowerPoint
			}),
			icon: { kind: "svg", name: "powerpoint" }
		}
	];
}

type LoadResultState = TryResult<readonly FileSystemItemModel[]>;

type None = {
	kind: "none";
	reason: string;
};

type SingleServer = {
	kind: "single";
	serverId: number;
	reason: string;
};

type AllServers = {
	kind: "all";
	reason?: string;
};

export type EnabledServer = None | SingleServer | AllServers;

export type File = {
	id: string;
	serverId: number;
	name: string;
	size: number;
};

export type Folder = {
	id: string;
	name: string;
	server: WebDavServerModel;
	parentId: string | null;
};